import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SiteConfigProvider, useSiteConfig } from './contexts/SiteConfigContext';
import { useCreationStore } from './store/useCreationStore';
import Navbar from './components/layout/Navbar';
import BottomNavigation from './components/layout/BottomNavigation';
import Home from './pages/Home';
import CreateWizard from './pages/create-wizard/CreateWizard';
import Stage1_Text from './pages/create/Stage1_Text';
import Stage2_Audio from './pages/create/Stage2_Audio';
import Stage3_Image from './pages/create/Stage3_Image';
import DIYEditor from './pages/diy-editor/DIYEditor';
import StoryProgress from './pages/StoryProgress';
import StoryViewer from './pages/StoryViewer';
import Library from './pages/Library';
import PublicLibrary from './pages/PublicLibrary';
import Reader from './pages/Reader/index';
import ShareViewer from './pages/ShareViewer';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Profile from './pages/Profile';

function MaintenancePage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
      <div className="text-6xl mb-6">🔧</div>
      <h1 className="text-3xl font-bold text-slate-800 mb-4">系统维护中</h1>
      <p className="text-slate-500 max-w-md">
        为了给您提供更好的服务，我们正在进行系统维护，请稍后再试。
      </p>
    </div>
  );
}

function Layout() {
  const location = useLocation();
  const { maintenanceMode, showCopyright, icpRecord, loading } = useSiteConfig();
  const isReader = location.pathname.startsWith('/reader');
  const isShare = location.pathname.startsWith('/share');
  const isCreateWizard = location.pathname === '/create';
  const isDIYEditor = location.pathname.startsWith('/create/diy/editor');
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isProfile = location.pathname.startsWith('/profile');

  // 手动 rehydrate creation store（避免与组件内 setState 竞争）
  useEffect(() => {
    useCreationStore.persist.rehydrate();
  }, []);

  // 特定页面隐藏外层导航
  const hideNavigation = isReader || isShare || isCreateWizard || isDIYEditor || isAuthPage || isProfile;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (maintenanceMode && !isAuthPage) {
    return <MaintenancePage />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {!hideNavigation && <Navbar />}

      <main className={`flex-1 w-full ${hideNavigation ? '' : 'pb-20 md:pb-0'}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile/*" element={<Profile />} />

          <Route path="/create" element={<PrivateRoute><CreateWizard /></PrivateRoute>} />
          <Route path="/create/stage/1" element={<PrivateRoute><Stage1_Text /></PrivateRoute>} />
          <Route path="/create/stage/2" element={<PrivateRoute><Stage2_Audio /></PrivateRoute>} />
          <Route path="/create/stage/3" element={<PrivateRoute><Stage3_Image /></PrivateRoute>} />
          <Route path="/create/diy/editor/:storyId" element={<PrivateRoute><DIYEditor /></PrivateRoute>} />
          <Route path="/progress/:storyId" element={<StoryProgress />} />
          <Route path="/story/:storyId" element={<StoryViewer />} />
          <Route path="/reader/:id" element={<Reader />} />
          <Route path="/library" element={<Library />} />
          <Route path="/public" element={<PublicLibrary />} />
          <Route path="/share/:token" element={<ShareViewer />} />
        </Routes>
      </main>

      {!hideNavigation && <BottomNavigation />}

      {/* 页脚 - 桌面端显示 */}
      {!hideNavigation && (
        <footer className="hidden md:block w-full border-t border-slate-200">
          <div className="w-full flex justify-center px-4 sm:px-6 lg:px-8 py-4">
            <div className="w-full max-w-7xl text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-2">
                <a href="#" className="hover:text-slate-600 transition-colors">关于我们</a>
                <span className="text-slate-200">|</span>
                <a href="#" className="hover:text-slate-600 transition-colors">帮助中心</a>
                <span className="text-slate-200">|</span>
                <a href="#" className="hover:text-slate-600 transition-colors">隐私政策</a>
                <span className="text-slate-200">|</span>
                <a href="#" className="hover:text-slate-600 transition-colors">联系我们</a>
              </div>
              {showCopyright && (
                <p className="text-sm text-slate-500 mb-1">© 2026 BadiBook - AI绘本生成器</p>
              )}
              {icpRecord && (
                <p className="text-sm text-slate-400">{icpRecord}</p>
              )}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

// 路由保护组件
function PrivateRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <Router>
      <SiteConfigProvider>
        <AuthProvider>
          <Layout />
        </AuthProvider>
      </SiteConfigProvider>
    </Router>
  );
}

export default App;
